import {BaseAlert, BaseBadge, BaseButton, BaseCard} from "@/components";
import {useWallet} from "./useWallet";
import type {WalletAccount} from "./wallet.types";

export interface WalletPageProps {
  onDeposit?: () => void;
  onWithdraw?: () => void;
  onTransfer?: () => void;
}

const WalletPage = ({onDeposit, onWithdraw, onTransfer}: WalletPageProps) => {
  const {wallet, isLoading, isError, error, refetch} = useWallet();

  if (isLoading) {
    return (
      <section className="mx-auto flex w-full max-w-[800px] flex-col gap-4 p-6">
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="m-0 text-center text-[var(--color-text-secondary)]">Cargando saldo…</p>
        </BaseCard>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="mx-auto flex w-full max-w-[800px] flex-col gap-4 p-6">
        <BaseAlert
          variant="danger"
          title="No pudimos cargar tu cartera"
        >
          {error?.message ?? "Inténtalo de nuevo en unos segundos."}
        </BaseAlert>
        <BaseButton
          variant="secondary"
          onClick={refetch}
        >
          Reintentar
        </BaseButton>
      </section>
    );
  }

  const defaultAccount = wallet?.defaultAccount ?? null;
  const otherAccounts: WalletAccount[] = wallet?.accounts.filter(a => a !== defaultAccount) ?? [];

  return (
    <section className="mx-auto flex w-full max-w-[800px] flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="m-0 text-[var(--font-h1)] font-bold text-[var(--color-text)]">Mi cartera</h1>
        <p className="m-0 text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Gestiona tu saldo y movimientos
        </p>
      </header>

      <BaseCard
        variant="elevated"
        padding="lg"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[var(--font-body)] uppercase tracking-wider text-[var(--color-text-secondary)]">
              Saldo disponible
            </span>
            {defaultAccount && <BaseBadge variant="brand">{defaultAccount.currency}</BaseBadge>}
          </div>
          <p className="m-0 text-[var(--font-display)] font-bold text-[var(--color-text)]">
            {defaultAccount?.formattedBalance ?? "—"}
          </p>
          {defaultAccount && defaultAccount.lockedBalance > 0 && (
            <p className="m-0 text-[var(--font-body)] text-[var(--color-text-tertiary)]">
              Bloqueado: {defaultAccount.formattedLockedBalance}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {onDeposit && (
              <BaseButton
                variant="primary"
                size="md"
                onClick={onDeposit}
              >
                Depositar
              </BaseButton>
            )}
            {onWithdraw && (
              <BaseButton
                variant="secondary"
                size="md"
                onClick={onWithdraw}
              >
                Retirar
              </BaseButton>
            )}
            {onTransfer && (
              <BaseButton
                variant="ghost"
                size="md"
                onClick={onTransfer}
              >
                Transferir
              </BaseButton>
            )}
          </div>
        </div>
      </BaseCard>

      {otherAccounts.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="m-0 text-[var(--font-h3)] font-bold text-[var(--color-text)]">Otras monedas</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {otherAccounts.map(account => (
              <BaseCard
                key={account.currency}
                variant="surface"
                padding="md"
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[var(--font-body)] uppercase tracking-wider text-[var(--color-text-secondary)]">
                      {account.currency}
                    </span>
                    <span className="text-[var(--font-h2)] font-bold text-[var(--color-text)]">
                      {account.formattedBalance}
                    </span>
                  </div>
                  <BaseBadge variant="neutral">{account.currency}</BaseBadge>
                </div>
              </BaseCard>
            ))}
          </div>
        </section>
      )}
    </section>
  );
};

export default WalletPage;
